import { useEffect, useState } from "react";
import useCompany from "../../../hooks/useCompany";
import useAPIKeys from "../../../hooks/useAPIKeys";
import toast from 'react-hot-toast';
import ModalConfirmDelete from "../../../components/modals/ModalConfirmDelete";
import { ClipLoader } from "react-spinners";
import { Button } from '@oc-labs/wt-storybook';
import ModalCreateAPIKey from "../../../components/modals/ModalCreateAPIKey";
import useModal from "../../../hooks/useModal";
import { Plus } from "lucide-react";

export default function APIKeys() {
    const { activeCompany } = useCompany();
    const { listAPIKeys, createAPIKey, deleteAPIKey } = useAPIKeys();
    const [apiKeys, setApiKeys] = useState([]);
    const [copiedKeyId, setCopiedKeyId] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [apiKeyToDelete, setApiKeyToDelete] = useState(null);
    const [loading, setLoading] = useState(true);
    const createApiKeyModal = useModal("createApiKey");

    useEffect(() => {
			const fetchKeys = async () => {
				try {
					setLoading(true);
					const { data, error } = await listAPIKeys({ organizationId: activeCompany.id });
					if (error) {
							toast.error("Failed to fetch API keys.");
					} else {
						setApiKeys(data?.apiKeys || []);
					}
				} catch (err) {
					toast.error("Failed to fetch API keys.");
					console.error(err);
				} finally {
					setLoading(false);
				}
			};
			if (activeCompany?.id) {
				fetchKeys();
			}
    }, [activeCompany]);

    const handleCreateKey = async (name) => {
			if (!name.trim()) {
				toast.error("Name is required");
				return;
			}

			if (!activeCompany?.id) {
				toast.error("No active company selected.");
				return;
			}

			try {
				const { data, error } = await createAPIKey({
					name,
					configId: "org-keys",
					organizationId: activeCompany.id,
				});
				if (error) {
					toast.error("Error creating API key.");
				} else {
					setApiKeys([data, ...apiKeys]);
					toast.success("API Key created successfully.");
				}
			} catch (error) {
				console.error("Error creating API key:", error);
				toast.error("Error creating API key.");
			}
    };

    const confirmDeleteKey = (id) => {
			setApiKeyToDelete(id);
			setIsDeleteModalOpen(true);
    };

    const deleteKey = async () => {
			try {
				const { error } = await deleteAPIKey(apiKeyToDelete, { configId: "org-keys" });
				if (error) {
					toast.error("Error deleting API key.");
				} else {
					setApiKeys(apiKeys.filter((k) => k.id !== apiKeyToDelete));
					setIsDeleteModalOpen(false);
					setApiKeyToDelete(null);
					toast.success("API Key deleted successfully.");
				}
			} catch (error) {
				console.error("Error deleting API key:", error);
				toast.error("Error deleting API key.");
			}
    };

    const handleDeleteKey = (id) => {
			confirmDeleteKey(id);
    }

    const handleCopySecret = async (secret, id) => {
			try {
				await navigator.clipboard.writeText(secret);
				setCopiedKeyId(id);
				setTimeout(() => setCopiedKeyId(null), 3000);
			} catch (err) {
				toast.error("Failed to copy secret.");
			}
    };

    if (loading || !activeCompany) {
			return (
				<div className="flex items-center justify-center w-full h-screen">
					<ClipLoader />
				</div>
			);
    }

    return (
			<div className="2xl:p-8 p-4">
				<div className="flex justify-between items-center mb-6">
					<div>
						<h2 className="2xl:text-2xl text-lg font-bold">API Keys</h2>
						<p className="text-slate-500">
							Manage your API keys for programmatic access.
						</p>
					</div>

					<div className="flex">
						<Button
							type="secondary"
							size="small"
							onClick={() => createApiKeyModal.open()}
							icon={<Plus size={16} />}
							className="whitespace-nowrap"
						>
							Create API Key
						</Button>
					</div>
				</div>

				<div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
					<table className="w-full text-left border-collapse">
						<thead>
							<tr className="bg-slate-50 border-b border-slate-200">
								<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Name
								</th>
								<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Created At
								</th>
								<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
									Secret
								</th>
								<th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">
									Actions
								</th>
							</tr>
						</thead>

						<tbody className="divide-y divide-slate-100">
							{apiKeys.map((key) => (
								<tr key={key.id} className="hover:bg-slate-50/50 transition-colors">
									<td className="px-6 py-4">
										<span className="font-semibold text-sm text-slate-900">{key.name}</span>
									</td>

									<td className="px-6 py-4">
										<span className="text-sm text-slate-700">
											{new Date(key.createdAt).toLocaleDateString()}
										</span>
									</td>

									<td className="px-6 py-4">
										{key.key && (
											<div className="flex items-center gap-2">
												<span className="font-mono text-xs bg-slate-100 text-slate-700 px-3 py-1.5 rounded-md">
													{key.key}
												</span>
												<div >
												<Button
													type="tertiary"
													size="small"
													onClick={() => handleCopySecret(key.key, key.id)}
													className="text-xs !px-2 !py-1"
												>
													{copiedKeyId === key.id ? "Copied!" : "Copy"}
												</Button>
												</div>
											</div>
										)}

										{!key.key && (
											<span className="text-slate-400 italic text-sm">
												Hidden
											</span>
										)}
									</td>

									<td className="px-6 py-4 text-right flex flex-col items-end gap-2">
										<div className="flex justify-end">
											<Button
												type="danger"
												size="small"
												onClick={() => handleDeleteKey(key.id)}
											>
												Delete
											</Button>
										</div>
									</td>
								</tr>
							))}

							{apiKeys.length === 0 && (
								<tr>
									<td colSpan={4} className="py-8 px-6 text-center text-slate-400 text-sm">
										No API keys found.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				<ModalCreateAPIKey onCreate={handleCreateKey} />

				<ModalConfirmDelete
					isOpen={isDeleteModalOpen}
					closeModal={() => setIsDeleteModalOpen(false)}
					onConfirm={deleteKey}
					title="Delete API Key"
					description="Are you sure you want to delete this API Key? This action cannot be undone."
				/>

			</div>
    );
}
